/**
 * $0 prices on the quote price sheet (L-0640, Nick 2026-10-14: "we can give things away", but the screen must make it
 * obvious). The 11 labor rates and every standard/fuel service price (#7122 review N3) may be saved at $0, but a field that BECOMES $0 needs a person's explicit,
 * per-field confirmation: the Pricing page shows a panel naming each one, and the server (quotePricingService) refuses
 * the save unless every newly-$0 field is named in `confirmZeroPrices`. A field already saved at $0 is not asked again.
 *
 * ONE list for the page and the server, so they can never disagree about which fields need a confirmation.
 * Customer-facing price rows of fees, samples, disposal, hazardous waste and charge-outs are covered the same way (#7612,
 * Nick 2026-10-18: "$0 on customer prices only"), and so is a fluid sample's maintenance-agreement price, which sits on
 * the same row and is a customer price too. Internal COSTS (an equipment item's cost, a part's cost) are NOT here:
 * they still refuse $0, because there a zero is a data error, not a giveaway.
 *
 * What the customer sees (Nick 2026-10-18: "Show a $0 / No charge line"): a SELECTED optional fee, hazardous-waste tier,
 * fluid sample or standard/fuel service priced at $0 prints on the quote marked "No charge" (customerDocument). See
 * `zeroPriceCustomerView` for the field-by-field truth; not every covered field is itemized on the document.
 *
 * NOT covered, and out of scope here (they predate this list and can already be saved at $0 with no prompt): mileage,
 * surcharge and trip rates, permit fees, rental delivery charges, and standardMaterials.price. Tracked in the PR body of #7685.
 */

export interface ZeroPriceField {
  /**
   * Stable key: `install.techRate`, `rates.laborRate`, or `<list path>.<row id>:<row name>.<price field>`, e.g.
   * `standardServices.svc-1:Megger.price`, `install.disposal.d1:Under 100 kW.price`,
   * `fluidSampling.oil:Oil Sampling.agreementPrice`. A row is keyed by id AND name: renaming a $0 row (or re-adding its id
   * as a different row) is a NEW $0 that needs its own confirmation.
   */
  path: string;
  /** What the person sees: the tab and the label on the Pricing page. */
  label: string;
}

/** `details.reason` on the server's refusal of an unconfirmed new $0. */
export const ZERO_PRICE_NEEDS_CONFIRM_REASON = 'zero-price-needs-confirm';
/** A confirmation longer than this is not a person's list; it counts as no confirmation. */
export const MAX_ZERO_PRICE_CONFIRMS = 500;

/**
 * A price below half a cent is $0: it prints as $0.00 on a quote, so it needs the same confirmation (RVW-34, #7347).
 * Prices are never negative (the schema refuses them).
 */
const isZero = (v: unknown): boolean => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v < 0.005;

/** The 6 installation labor rates covered here: key on `install`, and its label on the Pricing page. */
export const ZERO_PRICE_INSTALL_RATES: ReadonlyArray<readonly [string, string]> = [
  ['techRate', 'Field Technician (Elec. / Mech.)'],
  ['fieldLaborRate', 'Field Labor'],
  ['engineeringRate', 'Engineering'],
  ['projectManagementRate', 'Project Management'],
  ['adminRate', 'Administrative'],
  ['permittingLaborRate', 'Permitting Labor'],
];
/** The 5 service labor rates covered here: key on `rates`, and its label on the Pricing page. */
export const ZERO_PRICE_SERVICE_RATES: ReadonlyArray<readonly [string, string]> = [
  ['laborRate', 'Labor Rate (Field Technician)'],
  ['fieldEngineeringRate', 'Field Engineering'],
  ['generalLaborRate', 'General Labor'],
  ['engineeringRate', 'Engineering & Project Management'],
  ['adminRate', 'Administrative'],
];
/** The 2 service lists whose row `price` is covered here. */
export const ZERO_PRICE_SERVICE_LISTS: ReadonlyArray<readonly [string, string]> = [
  ['standardServices', 'Standard Services'],
  ['fuelServices', 'Fuel & Tank Services'],
];

/**
 * The customer-price row lists covered since #7612: where the list sits on the sheet, the key its rows are named by
 * (`name` or `label`), and the tab and title the Pricing page shows. Rows are keyed `<path>.<id>:<name>.price`.
 */
export const ZERO_PRICE_FEE_LISTS: ReadonlyArray<{
  path: readonly string[];
  nameKey: 'name' | 'label';
  title: string;
  /** A second customer price on the same row: the key, and how the page names it. */
  extra?: { key: string; label: string };
}> = [
  { path: ['otherFees'], nameKey: 'name', title: 'Service Pricing: Other Fees' },
  { path: ['fluidSampling'], nameKey: 'name', title: 'Service Pricing: Fluid Sampling', extra: { key: 'agreementPrice', label: 'maintenance agreement price' } },
  { path: ['hazWaste'], nameKey: 'label', title: 'Service Pricing: Hazardous Waste' },
  { path: ['equipmentChargeOuts'], nameKey: 'name', title: 'Service Pricing: Equipment Charge-Outs' },
  { path: ['install', 'disposal'], nameKey: 'label', title: 'Installation Pricing: Disposal' },
];

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/**
 * Every covered field at $0 (below half a cent) in this sheet. Reads defensively: a stored sheet may be partial or in an
 * older layout, and anything that is not a number (a blank "quote per job" service, a missing field) is not $0.
 */
export function zeroPriceFields(sheet: unknown): ZeroPriceField[] {
  const s = asRecord(sheet);
  const out: ZeroPriceField[] = [];
  const install = asRecord(s.install);
  for (const [key, label] of ZERO_PRICE_INSTALL_RATES) {
    if (isZero(install[key])) out.push({ path: `install.${key}`, label: `Installation Pricing: ${label}` });
  }
  const rates = asRecord(s.rates);
  for (const [key, label] of ZERO_PRICE_SERVICE_RATES) {
    if (isZero(rates[key])) out.push({ path: `rates.${key}`, label: `Service Pricing: ${label}` });
  }
  for (const [list, title] of ZERO_PRICE_SERVICE_LISTS) {
    const rows = Array.isArray(s[list]) ? (s[list] as unknown[]) : [];
    for (const raw of rows) {
      const row = asRecord(raw);
      if (isZero(row.price) && typeof row.id === 'string') {
        const name = typeof row.name === 'string' ? row.name.trim() : '';
        out.push({ path: `${list}.${row.id}:${name}.price`, label: `Service Pricing: ${title}, ${name || row.id}` });
      }
    }
  }
  for (const { path, nameKey, title, extra } of ZERO_PRICE_FEE_LISTS) {
    let node: unknown = s;
    for (const step of path) node = asRecord(node)[step];
    const rows = Array.isArray(node) ? (node as unknown[]) : [];
    for (const raw of rows) {
      const row = asRecord(raw);
      if (isZero(row.price) && typeof row.id === 'string') {
        const name = typeof row[nameKey] === 'string' ? (row[nameKey] as string).trim() : '';
        out.push({ path: `${path.join('.')}.${row.id}:${name}.price`, label: `${title}, ${name || row.id}` });
      }
      if (extra && isZero(row[extra.key]) && typeof row.id === 'string') {
        const name = typeof row[nameKey] === 'string' ? (row[nameKey] as string).trim() : '';
        out.push({ path: `${path.join('.')}.${row.id}:${name}.${extra.key}`, label: `${title}, ${name || row.id} (${extra.label})` });
      }
    }
  }
  return out;
}

/** The fields that are $0 in `next` but were not $0 in `saved` (the sheet being replaced; null/absent = never saved). */
export function newZeroPriceFields(next: unknown, saved: unknown): ZeroPriceField[] {
  const before = new Set(zeroPriceFields(saved).map((f) => f.path));
  return zeroPriceFields(next).filter((f) => !before.has(f.path));
}

/**
 * The newly-$0 fields the save did NOT confirm. `confirmed` is untrusted input: only an array of strings, at most
 * MAX_ZERO_PRICE_CONFIRMS long, counts; anything else is treated as no confirmation at all.
 */
export function unconfirmedZeroPrices(next: unknown, saved: unknown, confirmed: unknown): ZeroPriceField[] {
  const valid =
    Array.isArray(confirmed) &&
    confirmed.length <= MAX_ZERO_PRICE_CONFIRMS &&
    confirmed.every((c) => typeof c === 'string');
  const ok = new Set(valid ? (confirmed as string[]) : []);
  return newZeroPriceFields(next, saved).filter((f) => !ok.has(f.path));
}

/** What a customer sees of a $0 field on their quote: see `zeroPriceCustomerView`. */
export type ZeroPriceCustomerView = 'no-charge-line' | 'zero-rate-line' | 'not-itemized';

/**
 * What the customer's quote does with a covered field at $0 (the Pricing page says so beside each one, so it never
 * promises a line that is not there):
 * - `no-charge-line`: a selected optional fee, hazardous-waste tier, fluid sample or standard/fuel service prints as "No charge";
 * - `zero-rate-line`: a labor rate prints as an hourly row at $0.00 (only when hours are entered);
 * - `not-itemized`: a disposal tier, charge-out or maintenance-agreement price is not a line on the customer document
 *   (disposal is inside the totals; charge-outs and agreement sample prices are not read by any quote document yet).
 */
export function zeroPriceCustomerView(path: string): ZeroPriceCustomerView {
  if (path.startsWith('install.disposal.') || path.startsWith('equipmentChargeOuts.') || path.endsWith('.agreementPrice')) return 'not-itemized';
  if (path.startsWith('install.') || path.startsWith('rates.')) return 'zero-rate-line';
  return 'no-charge-line';
}

/**
 * The fields the server named in a `zero-price-needs-confirm` refusal (`error.details.fields`), or null when the error is
 * anything else or the list is malformed. Untrusted input: only well-formed `{path, label}` strings, at most
 * MAX_ZERO_PRICE_CONFIRMS of them.
 */
export function zeroPriceFieldsFromRefusal(error: unknown): ZeroPriceField[] | null {
  const details = asRecord(asRecord(error).details);
  if (details.reason !== ZERO_PRICE_NEEDS_CONFIRM_REASON || !Array.isArray(details.fields)) return null;
  if (details.fields.length === 0 || details.fields.length > MAX_ZERO_PRICE_CONFIRMS) return null;
  const out: ZeroPriceField[] = [];
  for (const raw of details.fields) {
    const f = asRecord(raw);
    if (typeof f.path !== 'string' || typeof f.label !== 'string' || f.path === '') return null;
    out.push({ path: f.path, label: f.label });
  }
  return out;
}
